import {
  AudioWaveform,
  BookOpen,
  Building2,
  CalendarClock,
  ClipboardCheck,
  Clock,
  Command,
  DollarSign,
  FileText,
  FolderKanban,
  GalleryVerticalEnd,
  GitBranch,
  LayoutDashboard,
  Target,
  UserCog,
  Users,
} from "lucide-react";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { SidebarData } from "../types";

// Alias for easier access in sidebar
const AppPermissions = Permissions;

export const sidebarData: SidebarData = {
  teams: [
    {
      name: "Shadcn Admin",
      logo: Command,
      plan: "Vite + ShadcnUI",
    },
    {
      name: "Acme Inc",
      logo: GalleryVerticalEnd,
      plan: "Enterprise",
    },
    {
      name: "Acme Corp.",
      logo: AudioWaveform,
      plan: "Startup",
    },
  ],
  navGroups: [
    {
      title: "ທົ່ວໄປ",
      items: [
        {
          title: "ແຜງຄວບຄຸມ",
          url: "/app/dashboard",
          icon: LayoutDashboard,
        },
      ],
    },
    {
      title: "ສູນກາງຂໍ້ມູນອົງກອນ",
      items: [
        {
          title: "ອົງກອນ",
          icon: Building2,
          items: [
            {
              title: "ຜັງອົງກອນ",
              url: "/app/org-chart",
              requiredPermissions: [AppPermissions["org-chart"].read],
            },
            {
              title: "ພະແນກ",
              url: "/app/departments",
              requiredPermissions: [AppPermissions.departments.read],
            },
            {
              title: "ຕໍາແໜ່ງ",
              url: "/app/positions",
              requiredPermissions: [AppPermissions.positions.read],
            },
            {
              title: "ກໍາຫນົດຕໍາແໜ່ງ",
              url: "/app/assign-position",
              requiredPermissions: [AppPermissions.assignments.read],
            },
          ],
        },
        {
          title: "ລາຍລະອຽດວຽກ (JD)",
          icon: FileText,
          items: [
            {
              title: "ລາຍການ JD",
              url: "/app/jd",
              requiredPermissions: [AppPermissions.jd.read],
            },
            {
              title: "ຜູກ JD ກັບຕໍາແໜ່ງ",
              url: "/app/jd-mapping",
              requiredPermissions: [AppPermissions.jd_mapping.read],
            },
          ],
        },
      ],
    },

    {
      title: "ຄູ່ມືການເຮັດວຽກ",
      items: [
        {
          title: "ຂັ້ນຕອນການດໍາເນີນງານ (SOP)",
          icon: BookOpen,
          items: [
            {
              title: "ລາຍການ SOP",
              url: "/app/sop",
              requiredPermissions: [AppPermissions.sop.read],
            },
            {
              title: "ການຜູກ SOP ກັບຕໍາແໜ່ງ",
              url: "/app/sop-mapping",
              requiredPermissions: [AppPermissions.sop.read],
            },
          ],
        },
        {
          title: "ລາຍການກວດສອບວຽກ",
          icon: ClipboardCheck,
          items: [
            {
              title: "ລາຍການ Checklist",
              url: "/app/checklists",
              requiredPermissions: [AppPermissions.checklists.read],
            },
            {
              title: "ປະຫວັດການບັນທຶກ",
              url: "/app/checklists/execution-history",
              requiredPermissions: [AppPermissions.checklists.read],
            },
            {
              title: "ສະຫຼຸບຜົນ Checklist",
              url: "/app/checklists/result",
              requiredPermissions: [AppPermissions.checklists.read],
            },
          ],
        },
        {
          title: "ການດໍາເນີນງານ (Flow)",
          url: "/app/flows",
          requiredPermissions: [AppPermissions.flows.read],
          icon: GitBranch,
        },
      ],
    },

    {
      title: "ມາດຕະຖານອົງກອນ",
      items: [
        {
          title: "ລາຍການ KPI",
          icon: Target,
          items: [
            {
              title: "ລາຍການ KPI",
              url: "/app/kpis",
              requiredPermissions: [AppPermissions.kpi.read],
            },
            {
              title: "ພາບລວມ KPI",
              url: "/app/management/okr-kpi",
              requiredPermissionsAny: [
                AppPermissions.mgmt_okr_kpi.read,
                AppPermissions.kpi.read,
              ],
            },
            {
              title: "ຄຳນວນ KPI ຕາມຊ່ວງເວລາ",
              url: "/app/management/okr-kpi/calculation",
              requiredPermissionsAny: [
                AppPermissions.mgmt_okr_kpi.read,
                AppPermissions.kpi.read,
              ],
            },
          ],
        },
        {
          title: "ມາດຕະຖານບໍລິການ",
          icon: FileText,
          items: [
            {
              title: "ມາດຕະຖານບໍລິການ",
              url: "/app/service-standard",
              requiredPermissions: [AppPermissions.service_standard.read],
            },
            {
              title: "ການຜູກມາດຕະຖານບໍລິການ",
              url: "/app/service-standard-mapping",
              requiredPermissions: [AppPermissions.service_standard.read],
            },
          ],
        },
        {
          title: "ມາດຕະຖານ  QC",
          icon: ClipboardCheck,
          items: [
            {
              title: "ລາຍການ QC",
              url: "/app/qc",
              requiredPermissions: [AppPermissions.qc.read],
              // icon: ClipboardCheck,
            },
            {
              title: "ບັນທຶກການກວດສອບ QC",
              url: "/app/qc/records",
              requiredPermissions: [AppPermissions.qc_record.read],
              // icon: ClipboardCheck,
            },
            {
              title: "ສະຫຼຸບຜົນ QC",
              url: "/app/qc/result",
              requiredPermissions: [AppPermissions.qc_result.read],
              // icon: ClipboardCheck,
            },
          ],
        },
        {
          title: "ຜະແນການກວດສອບພາຍໃນ",
          icon: ClipboardCheck,
          items: [
            {
              title: "ລາຍການຜະແນການກວດສອບພາຍໃ�",
              url: "/app/internal-audit",
              requiredPermissions: [AppPermissions.internal_audit.read],
            },
            {
              title: "ບັນທຶກການກວດສອບພາຍໃນ",
              url: "/app/internal-audit/records",
              requiredPermissions: [AppPermissions.internal_audit.create],
            },
            {
              title: "ສະຫຼຸບຜົນກວດສອບພາຍໃນ",
              url: "/app/internal-audit/summary",
              requiredPermissions: [AppPermissions.internal_audit.summary],
            },
          ],
        },
      ],
    },

    {
      title: "ສູນເອກະສານ",
      items: [
        {
          title: "ຮ່າງເອກະສານ",
          icon: FileText,
          items: [
            {
              title: "ຮ່າງເອກະສານທັງໝົດ",
              url: "/app/template",
              requiredPermissions: [AppPermissions.template.read],
            },
            {
              title: "ຮ່າງເອກະສານຕາມພະແນກ",
              url: "/app/template/by-department",
              requiredPermissions: [AppPermissions.template.read],
            },
          ],
        },
        {
          title: "ສູນຟອມ",
          icon: FileText,
          items: [
            {
              title: "ລາຍການຟອມ",
              url: "/app/forms",
              requiredPermissions: [AppPermissions.forms.read],
            },
            {
              title: "ຫມວດຫມູ່ຟອມ",
              url: "/app/forms/categories",
              requiredPermissions: [AppPermissions.forms.read],
            },
          ],
        },
        {
          title: "ສັນຍາ",
          icon: ClipboardCheck,
          items: [
            {
              title: "ລາຍການສັນຍາ",
              url: "/app/contracts",
              requiredPermissions: [AppPermissions.contracts.read],
            },
            {
              title: "ສັນຍາຈະໝົດອາຍຸ",
              url: "/app/contracts/expiring",
              requiredPermissions: [AppPermissions.contracts.read],
            },
          ],
        },
        {
          title: "ເອກະສານການເງິນ",
          icon: FileText,
          items: [
            {
              title: "ລາຍການເອກະສານ",
              url: "/app/documents",
              requiredPermissions: [AppPermissions.quotation_invoice.read],
            },
            {
              title: "ປະຫວັດການກວດສອບເອກະສານ",
              url: "/app/documents/audit",
              requiredPermissions: [AppPermissions.quotation_invoice.read],
            },
          ],
        },
      ],
    },

    {
      title: "HR ZONE",
      items: [
        {
          title: "ລາຍຊື່ພະນັກງານ",
          icon: Users,
          url: "/app/hr/employees",
          requiredPermissions: [AppPermissions.hr_employee_profile.list],
        },
        {
          title: "ເງິນເດືອນ",
          icon: DollarSign,
          items: [
            {
              title: "ລາຍການເງິນເດືອນ",
              url: "/app/hr/payroll",
              requiredPermissions: [AppPermissions.hr_payroll.list],
            },
            {
              title: "ເງິນເດືອນຕາມໄລຍະ",
              url: "/app/hr/payroll/period",
              requiredPermissions: [AppPermissions.hr_payroll.list],
            },
            {
              title: "ປະຫວັດເງິນເດືອນ",
              url: "/app/hr/payroll/history",
              requiredPermissions: [AppPermissions.hr_payroll.list],
            },
          ],
        },
        {
          title: "ລ່ວງເວລາ",
          icon: Clock,
          items: [
            {
              title: "ຂໍ້ກຳນົດ OT",
              url: "/app/hr/overtime/rules",
              requiredPermissions: [AppPermissions.hr_overtime.rules_read],
            },
            {
              title: "ຄຳຂໍ OT ຂອງຂ້ອຍ",
              url: "/app/hr/overtime/requests",
              requiredPermissions: [AppPermissions.hr_overtime.request_list],
            },
            {
              title: "ລາຍການອະນຸມັດ OT",
              url: "/app/hr/overtime/approvals",
              requiredPermissions: [AppPermissions.hr_overtime.approve],
            },
            {
              title: "ສະຫຼຸບ OT ຕາມເດືອນ",
              url: "/app/hr/overtime/summary",
              requiredPermissions: [AppPermissions.hr_overtime.summary],
            },
          ],
        },
        {
          title: "ລະບົບການລາ",
          icon: CalendarClock,
          items: [
            {
              title: "ປະເພດການລາ",
              url: "/app/hr/leave/types",
              requiredPermissions: [AppPermissions.hr_leave.types_read],
            },
            {
              title: "ສິດລາຕໍ່ປີ",
              url: "/app/hr/leave/entitlements",
              requiredPermissions: [AppPermissions.hr_leave.entitlements_read],
            },
            {
              title: "ລາຍການອະນຸມັດລາ",
              url: "/app/hr/leave/approvals",
              requiredPermissions: [
                AppPermissions.hr_leave.approve,
                AppPermissions.hr_leave.approve_line,
              ],
            },
            {
              title: "ປະຫວັດການລາ / ສະຫຼຸບ",
              url: "/app/hr/leave/history",
              requiredPermissions: [AppPermissions.hr_leave.history],
            },
          ],
        },
        {
          title: "ນະໂຍບາຍບໍລິສັດ",
          icon: FileText,
          items: [
            {
              title: "ລາຍການນະໂຍບາຍ",
              url: "/app/hr/company-policy",
              requiredPermissions: [AppPermissions.hr_company_policy.read],
            },
            {
              title: "ນະໂຍບາຍເກັບໄວ້",
              url: "/app/hr/company-policy/archive",
              requiredPermissions: [AppPermissions.hr_company_policy.read],
            },
          ],
        },
        {
          title: "ເສັ້ນທາງການເຕີບໂຕ",
          icon: FileText,
          items: [
            {
              title: "ຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ",
              url: "/app/hr/promotion/rules",
              requiredPermissions: [AppPermissions.hr_promotion.rule_read],
            },
            {
              title: "ລາຍການບັນທຶກການເຕີບໂຕ",
              url: "/app/hr/promotion/records",
              requiredPermissions: [AppPermissions.hr_promotion.record_read],
            },
            {
              title: "ປະຫວັດການເຕີບໂຕ",
              url: "/app/hr/promotion/history",
              requiredPermissions: [AppPermissions.hr_promotion.history],
            },
          ],
        },
        {
          title: "ເບິກເງິນເດືອນລ່ວງໜ້າ",
          icon: DollarSign,
          items: [
            {
              title: "ສ້າງຄຳຂໍໃຫ້ພະນັກງານ",
              url: "/app/hr/salary-advance/new",
              requiredPermissions: [AppPermissions.hr_salary_advance.approve],
            },
            {
              title: "ລາຍການຄຳຂໍທັງໝົດ",
              url: "/app/hr/salary-advance",
              requiredPermissions: [
                AppPermissions.hr_salary_advance.list,
                AppPermissions.hr_salary_advance.approve,
              ],
            },
          ],
        },
      ],
    },

    {
      title: "MANAGEMENT ZONE",
      items: [
        {
          title: "ບັນທຶກການປະຊຸມ",
          icon: CalendarClock,
          items: [
            {
              title: "ບັນທຶກການປະຊຸມ",
              url: "/app/management/meetings",
              requiredPermissions: [
                AppPermissions.mgmt_meeting.read,
                AppPermissions.mgmt_meeting.list,
              ],
            },
            {
              title: "ຕິດຕາມປະເດັນທີ່ລົມກັນ",
              url: "/app/management/meetings/actions",
              requiredPermissions: [AppPermissions.mgmt_meeting.read],
            },
          ],
        },
        {
          title: "ການຈັດການໂຄງການ",
          icon: FolderKanban,
          items: [
            {
              title: "ລາຍການໂຄງການ",
              url: "/app/management/projects",
              requiredPermissions: [AppPermissions.mgmt_project.read],
            },
            {
              title: "ສະຫຼຸບຜົນ",
              url: "/app/management/projects/summary",
              requiredPermissions: [AppPermissions.mgmt_project.read],
            },
          ],
        },
        {
          title: "ປະຕິທິນວຽກ",
          icon: CalendarClock,
          items: [
            {
              title: "ປະຕິທິນວຽກ",
              url: "/app/management/calendar",
              requiredPermissions: [AppPermissions.mgmt_calendar.read],
            },
            {
              title: "ກຳນົດວັນພັກ",
              url: "/app/management/calendar/settings",
              requiredPermissions: [AppPermissions.mgmt_calendar.settings],
            },
          ],
        },
        {
          title: "ລາຍງານປະຈໍາເດືອນ",
          icon: FileText,
          url: "/app/management/monthly-reports",
          requiredPermissions: [AppPermissions.mgmt_monthly_report.read],
        },
      ],
    },

    {
      title: "ການຄວບຄຸມການເຂົ້າເຖິງ",
      items: [
        {
          title: "ບົດບາດ",
          url: "/app/roles",
          icon: UserCog,
          requiredPermissions: ["users:read"],
        },
        {
          title: "ຜູ້ໃຊ້",
          url: "/app/users",
          icon: Users,
          requiredPermissions: ["users:read"],
        },
        // {
        //   title: "ບັນທຶກການກວດກາ",
        //   url: "/app/audit",
        //   icon: ShieldCheck,
        //   requiredPermissions: ["audit:read"],
        // },
      ],
    },
  ],
};
